import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { CSSProperties } from "react";
import { profile } from "@/data/profile";
import type { Lang } from "@/lib/i18n";
import { makePresets } from "@/lib/motionPresets";
import { useClock } from "@/hooks/useClock";
import { useCopy } from "@/hooks/useCopy";
import { usePhase } from "@/hooks/usePhase";
import { Sky } from "./Sky";
import { Header } from "./Header";
import { Identity } from "./Identity";
import { Footer } from "./Footer";
import { SectionView } from "./SectionView";
import type { SectionCtx } from "./types";

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Root of the personal page. Owns the only three pieces of state:
 *   lang     — active language (PT/EN)
 *   activeId — which section is shown
 *   kick     — bumped on every section change to make the gears lurch
 */
export function TimerCard() {
  const reduced = useReducedMotion() ?? false;
  const presets = useMemo(() => makePresets(reduced), [reduced]);

  const [lang, setLang] = useState<Lang>("pt");
  const [activeId, setActiveId] = useState<string>(profile.sections[0]?.id ?? "");
  const [kick, setKick] = useState(0);

  const clock = useClock(profile.clock.timezone);
  const phaseControl = usePhase(clock.localHour);
  const { copied, copy } = useCopy();

  // Whir the gears faster whenever the phase flips (time visibly "moving on").
  const [burst, setBurst] = useState(0);
  const prevPhase = useRef(phaseControl.phase.id);
  useEffect(() => {
    if (prevPhase.current !== phaseControl.phase.id) {
      prevPhase.current = phaseControl.phase.id;
      setBurst((b) => b + 1);
    }
  }, [phaseControl.phase.id]);

  const activeIndex = Math.max(0, profile.sections.findIndex((s) => s.id === activeId));
  const active = profile.sections[activeIndex] ?? profile.sections[0];

  const select = (id: string) => {
    if (id === activeId) return;
    setActiveId(id);
    setKick((k) => k + 1);
  };

  const ctx: SectionCtx = { lang, copied, copy, presets };
  const rootStyle = { background: "var(--tc-page)", ...phaseControl.phase.tokens } as CSSProperties;

  return (
    <div
      className="timercard relative flex min-h-dvh w-full flex-col overflow-x-hidden md:h-dvh md:overflow-hidden"
      style={rootStyle}
    >
      <Sky scene={phaseControl.phase.scene} kick={kick} burst={burst} />

      <Header lang={lang} setLang={setLang} activeId={activeId} onSelect={select} phaseControl={phaseControl} />

      {/* stage */}
      <div className="relative z-[2] mx-auto flex w-full max-w-[1180px] flex-1 flex-col items-center gap-8 px-6 py-6 md:min-h-0 md:flex-row md:gap-[52px] md:px-12">
        <Identity lang={lang} clock={clock} phase={phaseControl.phase} />

        <div className="relative flex w-full min-w-0 flex-1 items-center md:h-full">
          <AnimatePresence mode="wait">
            {active && (
              <motion.div
                key={active.id}
                variants={presets.panel}
                initial="initial"
                animate="animate"
                exit="exit"
                style={{ width: "100%" }}
              >
                <SectionView section={active} number={pad2(activeIndex + 1)} ctx={ctx} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Footer lang={lang} />
    </div>
  );
}
